import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { SpanishTooltip } from '../components/charts/SpanishTooltip';
import { clearReport, loadEquipment, loadMaintenanceReport } from '../features/maintenance/maintenanceSlice';
import type { MaintenanceOrderType, MaintenanceReportRow } from '../features/maintenance/types';
import { AppDispatch, RootState } from '../store/store';
import { selectCurrentUser } from '../features/auth/authSlice';
import { ExportToExcel, ExcelColumn } from '../components/ExportToExcel';

const ORDER_TYPE_LABEL: Record<MaintenanceOrderType, string> = {
  preventivo: 'Preventivo',
  correctivo: 'Correctivo',
  calibracion: 'Calibración',
  inspeccion: 'Inspección',
};

const EXPORT_COLUMNS: ExcelColumn<MaintenanceReportRow>[] = [
  { header: 'Tipo', accessor: (row) => ORDER_TYPE_LABEL[row.type] },
  { header: 'Órdenes', accessor: 'orders' },
  { header: 'Horas totales', accessor: 'totalHours' },
  { header: 'Horas promedio', accessor: 'avgHours' },
  { header: 'Costo total', accessor: 'totalCost' },
];

function formatHours(value: number): string {
  return `${value.toFixed(2)} h`;
}

function formatCost(value: number): string {
  return value.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
}

interface ReportFiltersValue {
  equipmentId: string;
  from: string;
  to: string;
}

function ReportFilters({
  value,
  onChange,
}: {
  value: ReportFiltersValue;
  onChange: (next: ReportFiltersValue) => void;
}) {
  const { equipment } = useSelector((s: RootState) => s.maintenance);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 border rounded-md p-3 bg-muted/30">
      <label className="block">
        <span className="block text-xs font-medium text-muted-foreground mb-1">Equipo</span>
        <select
          className="border rounded-md px-2 py-1.5 w-full"
          value={value.equipmentId}
          onChange={(e) => onChange({ ...value, equipmentId: e.target.value })}
        >
          <option value="">Todos los equipos</option>
          {equipment.map((eq) => (
            <option key={eq.id} value={eq.id}>
              {eq.code} — {eq.name}
            </option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="block text-xs font-medium text-muted-foreground mb-1">Desde</span>
        <input
          type="date"
          className="border rounded-md px-2 py-1.5 w-full"
          value={value.from}
          onChange={(e) => onChange({ ...value, from: e.target.value })}
        />
      </label>
      <label className="block">
        <span className="block text-xs font-medium text-muted-foreground mb-1">Hasta</span>
        <input
          type="date"
          className="border rounded-md px-2 py-1.5 w-full"
          value={value.to}
          onChange={(e) => onChange({ ...value, to: e.target.value })}
        />
      </label>
    </div>
  );
}

function ReportTable({ rows, totals }: { rows: MaintenanceReportRow[]; totals: { orders: number; totalHours: number; totalCost: number } }) {
  return (
    <div className="border rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Tipo</th>
            <th className="text-right px-3 py-2 font-medium">Órdenes</th>
            <th className="text-right px-3 py-2 font-medium">Horas totales</th>
            <th className="text-right px-3 py-2 font-medium">Horas promedio</th>
            <th className="text-right px-3 py-2 font-medium">Costo total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.type} className="border-t">
              <td className="px-3 py-2">{ORDER_TYPE_LABEL[row.type]}</td>
              <td className="px-3 py-2 text-right">{row.orders}</td>
              <td className="px-3 py-2 text-right">{formatHours(row.totalHours)}</td>
              <td className="px-3 py-2 text-right">{formatHours(row.avgHours)}</td>
              <td className="px-3 py-2 text-right">{formatCost(row.totalCost)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t bg-muted/50 font-medium">
            <td className="px-3 py-2">Totales</td>
            <td className="px-3 py-2 text-right">{totals.orders}</td>
            <td className="px-3 py-2 text-right">{formatHours(totals.totalHours)}</td>
            <td className="px-3 py-2 text-right">—</td>
            <td className="px-3 py-2 text-right">{formatCost(totals.totalCost)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function ReportCharts({ rows }: { rows: MaintenanceReportRow[] }) {
  const chartData = rows.map((row) => ({
    name: ORDER_TYPE_LABEL[row.type],
    Órdenes: row.orders,
    Horas: row.totalHours,
    Costo: row.totalCost,
  }));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
        <h3 className="text-sm font-semibold mb-4">Órdenes y horas por tipo</h3>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" />
              <YAxis />
              <SpanishTooltip />
              <Legend />
              <Bar dataKey="Órdenes" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Horas" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
        <h3 className="text-sm font-semibold mb-4">Costo total por tipo</h3>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" />
              <YAxis />
              <SpanishTooltip formatter={(v) => formatCost(typeof v === 'number' ? v : 0)} />
              <Legend />
              <Bar dataKey="Costo" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export function MaintenanceReportsPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { report, reportLoading } = useSelector((s: RootState) => s.maintenance);
  const currentUser = useSelector(selectCurrentUser);
  const canExportExcel = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const [filters, setFilters] = useState<ReportFiltersValue>({ equipmentId: '', from: '', to: '' });

  useEffect(() => {
    dispatch(loadEquipment({ pageSize: 200, status: undefined }));
    return () => {
      dispatch(clearReport());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      loadMaintenanceReport({
        equipmentId: filters.equipmentId || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined,
      }),
    );
  }, [dispatch, filters.equipmentId, filters.from, filters.to]);

  const hasData = useMemo(() => (report ? report.totals.orders > 0 : false), [report]);

  return (
    <div className="space-y-4">
      <div>
        <Link to="/mantenimiento/equipos" className="text-sm text-primary hover:underline">
          ← Volver a equipos
        </Link>
      </div>
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Reportes de mantenimiento</h1>
          <p className="text-sm text-muted-foreground">
            Comparativo preventivo vs correctivo: cantidades, horas y costos por tipo de orden.
          </p>
        </div>
        {canExportExcel && report && (
          <ExportToExcel
            data={report.byType}
            filename="reporte_mantenimiento_preventivo_vs_correctivo"
            columns={EXPORT_COLUMNS}
            disabled={reportLoading || !hasData}
          />
        )}
      </header>

      <ReportFilters value={filters} onChange={setFilters} />

      {reportLoading && <p className="text-muted-foreground">Cargando reporte…</p>}

      {!reportLoading && report && !hasData && (
        <div className="border rounded-md p-6 text-center text-muted-foreground bg-card">
          No hay órdenes de mantenimiento para los filtros seleccionados.
        </div>
      )}

      {!reportLoading && report && hasData && (
        <div className="space-y-6">
          <ReportTable rows={report.byType} totals={report.totals} />
          <ReportCharts rows={report.byType} />
          <p className="text-xs text-muted-foreground">
            Las horas se calculan sobre registros vinculados a una orden; los registros sin orden no se
            incluyen. El rango de fechas se aplica sobre la fecha programada de la orden.
          </p>
        </div>
      )}
    </div>
  );
}
